import React from 'react';
import {
  IconLoader2,
  IconPlayerPauseFilled,
  IconPlayerPlayFilled,
  IconPlayerStopFilled,
  IconSend,
  IconX,
  IconRepeat,
} from '@tabler/icons-react';
import Webcam from 'react-webcam';
import { toast } from 'react-toastify';
import { canSwitchCamera } from '@common/media';
import getTextColor from '../../../hooks/getTextColorContrast';
import { cn } from '../../../lib/utils';
import VideoCount from './VideoCount';
import { translations } from '../../../translations/chatInputTranslations';

enum EVideoStatus {
  IDLE = 'idle',
  RECORDING = 'recording',
  PAUSED = 'paused',
  STOPPED = 'stopped',
}

const DEFAULT_PRIMARY_COLOR = '#096846';

interface IVideoProps {
  stream: React.RefObject<Webcam>;
  themeData: ICreateThemeData;
  handleSubmitVideo: () => void;
  isTestChat: boolean;
  isFrontCamera: boolean;
  previewStream: React.RefObject<Webcam>;
  videoPreviewUrl: string | undefined;
  handleStartVideoRecording: () => void;
  handleFlipCamera: () => void;
  statusVideo: string;
  handleStopVideoRecording: () => void;
  handlePauseVideoRecording: () => void;
  handleResumeVideoRecording: () => void;
  handleExitVideoRecording: () => void;
  capturing: boolean;
  handleCloseVideoModal: () => void;
  setShowVoiceMainInput: (value: boolean) => void;
  isVideoSending: boolean;
  isVideoStopping: boolean;
  isVideoPausing: boolean;
  isVideoExiting: boolean;
  isVideoStarting: boolean;
  isVideoResuming: boolean;
  isSwitchingCamera: boolean;
  getVideoConstraints: () => any;
  languageCode?: string;
}

function VideoPreview({
  themeData,
  isTestChat,
  isFrontCamera,
  previewStream,
  videoPreviewUrl,
  handleStartVideoRecording,
  handleFlipCamera,
  statusVideo,
  handleStopVideoRecording,
  handlePauseVideoRecording,
  handleResumeVideoRecording,
  handleExitVideoRecording,
  capturing,
  handleSubmitVideo,
  handleCloseVideoModal,
  setShowVoiceMainInput,
  isVideoSending,
  isVideoStopping,
  isVideoPausing,
  isVideoExiting,
  isVideoStarting,
  isVideoResuming,
  isSwitchingCamera,
  getVideoConstraints,
  languageCode = 'en',
}: IVideoProps) {
  // Get translations for current language
  const t = translations[languageCode] || translations.en;

  // Use shared utility for mobile detection
  const isMobileCameraSwitch = canSwitchCamera(true);

  // Handle camera access errors
  const handleCameraError = (error: string | DOMException) => {
    console.log('❌ Camera access error', { error, isFrontCamera });
    // Don't show error toast if we're currently switching cameras
    if (isSwitchingCamera) {
      console.log('🔄 Ignoring error during camera switch');
      return;
    }
    toast.error(t.cameraAccessError);
    handleCloseVideoModal();
    setShowVoiceMainInput(true);
  };

  return (
    <div
      className={cn(
        isTestChat
          ? 'absolute max-w-[350px] z-100 w-full h-[350px] mx-2 mb-2 -translate-x-1/2 bottom-28 left-1/2'
          : 'absolute max-w-[500px] z-100 min500:max-w-full min500:h-[100dvh] min500:top-0 min500:mx-0 w-full min500:mb-0 min500:rounded-none min500:bottom-[calc(0px-77px)] h-[500px] mx-2 mb-2 -translate-x-1/2 bottom-8 left-1/2',
      )}
    >
      <div
        style={{ backgroundColor: themeData?.['chat-primary'] || '#FF6F98' }}
        className="relative flex flex-col justify-between w-full h-full gap-4 p-4 min500:rounded-none z-100 rounded-xl"
      >
        {statusVideo !== EVideoStatus.STOPPED && (
          <Webcam
            key={`${isFrontCamera ? 'front' : 'back'}-camera`}
            ref={previewStream}
            mirrored={isFrontCamera}
            playsInline
            videoConstraints={getVideoConstraints()}
            onUserMediaError={handleCameraError}
            audioConstraints={{
              sampleSize: 16,
              echoCancellation: true,
              noiseSuppression: true,
            }}
            audio
            muted
            className="object-cover w-full h-full max-h-[400px] min500:mt-1 rounded-xl min500:!max-h-[calc(100dvh-100px)]"
          />
        )}

        {statusVideo == EVideoStatus.STOPPED && (
          <div className="relative w-full h-full max-h-[400px] min500:!max-h-[calc(100dvh-100px)]">
            <p className="absolute px-2 py-1 text-sm font-semibold text-black -translate-x-1/2 bg-white rounded-full top-4 left-1/2">
              {t.preview}
            </p>

            <video
              src={videoPreviewUrl}
              controls
              playsInline
              preload="metadata"
              onLoadedMetadata={(e) => {
                const video = e.target as HTMLVideoElement;
                video.currentTime = 0.1; // Show first frame
              }}
              onError={() => {
                console.error('Video preview error');
              }}
              className="object-cover w-full h-full max-h-[400px] min500:mt-1 rounded-xl min500:!max-h-[calc(100dvh-100px)]"
            />
          </div>
        )}

        <button
          onClick={handleExitVideoRecording}
          type="button"
          className="absolute p-1 bg-white border-2 border-black rounded-full top-2 right-2"
        >
          <IconX size={20} className="text-black" />
        </button>

        {statusVideo === EVideoStatus.PAUSED && (
          <span className="absolute -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2 ">
            <IconPlayerPauseFilled className="text-white/30 mb-14" size={100} />
          </span>
        )}

        <div className="grid grid-cols-2 gap-3 w-full min-h-[40px]">
          {statusVideo !== EVideoStatus.STOPPED && !isVideoStopping && (
            <div className="col-span-2 flex items-center w-full gap-3 transition-all duration-200 ease-in-out">
              {statusVideo === EVideoStatus.PAUSED && (
                <button
                  onClick={handleResumeVideoRecording}
                  style={{
                    backgroundColor: themeData?.primary || DEFAULT_PRIMARY_COLOR,
                    color: getTextColor(themeData?.primary || DEFAULT_PRIMARY_COLOR),
                  }}
                  disabled={isVideoResuming}
                  type="button"
                  className="flex items-center justify-center w-full gap-2 px-5 py-2 text-white rounded-full disabled:opacity-70 transition-all duration-200 ease-in-out"
                >
                  {isVideoResuming ? (
                    <IconLoader2 size={22} className="animate-spin" />
                  ) : (
                    <IconPlayerPlayFilled className="mt-0.5" size={22} />
                  )}
                  {isVideoResuming ? t.resuming : t.continue}
                </button>
              )}

              {capturing && statusVideo !== EVideoStatus.PAUSED && (
                <button
                  onClick={handlePauseVideoRecording}
                  style={{
                    backgroundColor: themeData?.primary || DEFAULT_PRIMARY_COLOR,
                    color: getTextColor(themeData?.primary || DEFAULT_PRIMARY_COLOR),
                  }}
                  type="button"
                  disabled={isVideoPausing}
                  className="flex items-center justify-center w-full gap-2 px-5 py-2 text-white rounded-full disabled:opacity-70 transition-all duration-200 ease-in-out"
                >
                  {isVideoPausing ? (
                    <IconLoader2 size={22} className="animate-spin" />
                  ) : (
                    <IconPlayerPauseFilled className="mt-0.5" size={20} />
                  )}
                  {isVideoPausing ? t.pausing : t.pause}
                </button>
              )}

              {(capturing || statusVideo === EVideoStatus.PAUSED) && (
                <button
                  onClick={handleStopVideoRecording}
                  style={{
                    backgroundColor: themeData?.primary || DEFAULT_PRIMARY_COLOR,
                    color: getTextColor(themeData?.primary || DEFAULT_PRIMARY_COLOR),
                  }}
                  disabled={isVideoStopping}
                  type="button"
                  className="flex items-center justify-center w-full gap-2 px-5 py-2 text-white rounded-full disabled:opacity-70 transition-all duration-200 ease-in-out"
                >
                  {isVideoStopping ? (
                    <IconLoader2 size={22} className="animate-spin" />
                  ) : (
                    <IconPlayerStopFilled className="mt-0.5" size={20} />
                  )}
                  {isVideoStopping ? t.stopping : t.stop}
                </button>
              )}

              {/* flip camera */}
              {isMobileCameraSwitch && statusVideo === EVideoStatus.IDLE && (
                <button
                  onClick={() => {
                    console.log('🔄 Camera flip button clicked', { isFrontCamera });
                    handleFlipCamera();
                  }}
                  disabled={isSwitchingCamera}
                  style={{
                    backgroundColor: themeData?.primary || DEFAULT_PRIMARY_COLOR,
                    color: getTextColor(themeData?.primary || DEFAULT_PRIMARY_COLOR),
                  }}
                  type="button"
                  className="flex whitespace-nowrap items-center w-full justify-center gap-2 px-2.5 py-2 text-white rounded-full disabled:opacity-70 transition-all duration-200 ease-in-out"
                >
                  {isSwitchingCamera ? (
                    <IconLoader2 size={22} className="animate-spin mt-0.5" />
                  ) : (
                    <IconRepeat size={22} className="mt-0.5" />
                  )}
                  {isSwitchingCamera ? t.switching : t.switchCamera}
                </button>
              )}

              {/* start recording */}
              {statusVideo !== EVideoStatus.RECORDING &&
                statusVideo !== EVideoStatus.PAUSED &&
                !isVideoStopping && (
                  <button
                    onClick={handleStartVideoRecording}
                    style={{
                      backgroundColor: themeData?.primary || DEFAULT_PRIMARY_COLOR,
                      color: getTextColor(themeData?.primary || DEFAULT_PRIMARY_COLOR),
                    }}
                    disabled={isVideoStarting}
                    type="button"
                    className="flex items-center justify-center w-full gap-2 px-5 py-2 text-white rounded-full disabled:opacity-70 transition-all duration-200 ease-in-out"
                  >
                    {isVideoStarting ? (
                      <IconLoader2 size={22} className="animate-spin" />
                    ) : (
                      <div className="flex items-center justify-center w-5 h-5 bg-red-600 rounded-full">
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      </div>
                    )}
                    {isVideoStarting ? t.starting : t.record}
                  </button>
                )}

              {(capturing || statusVideo === EVideoStatus.PAUSED) && (
                <VideoCount
                  handleStopVideoRecording={handleStopVideoRecording}
                  isPaused={statusVideo === EVideoStatus.PAUSED}
                  isStarted={statusVideo === EVideoStatus.RECORDING}
                />
              )}
            </div>
          )}

          {(statusVideo === EVideoStatus.STOPPED || isVideoStopping) && (
            <button
              onClick={handleExitVideoRecording}
              style={{
                backgroundColor: themeData?.primary || DEFAULT_PRIMARY_COLOR,
                color: getTextColor(themeData?.primary || DEFAULT_PRIMARY_COLOR),
              }}
              disabled={isVideoExiting}
              type="button"
              className="flex items-center justify-center gap-2 px-5 py-2 text-white rounded-full disabled:opacity-70"
            >
              {isVideoExiting ? (
                <IconLoader2 size={20} className="animate-spin" />
              ) : (
                <IconX size={22} className="mt-0.5" />
              )}
              {isVideoExiting ? t.canceling : t.cancel}
            </button>
          )}

          {(statusVideo === EVideoStatus.STOPPED || isVideoStopping) && (
            <button
              onClick={handleSubmitVideo}
              style={{
                backgroundColor: themeData?.primary || DEFAULT_PRIMARY_COLOR,
                color: getTextColor(themeData?.primary || DEFAULT_PRIMARY_COLOR),
              }}
              disabled={isVideoSending || isVideoStopping}
              type="button"
              className="flex items-center justify-center gap-2 px-5 py-2 text-white rounded-full disabled:opacity-70"
            >
              {isVideoSending ? (
                <IconLoader2 size={20} className="animate-spin" />
              ) : (
                <IconSend size={22} />
              )}
              {isVideoSending ? t.sending : t.send}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default VideoPreview;
